import React, { useState, useEffect } from "react";
import { Table, Modal, Spinner } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { useSelector, useDispatch } from "react-redux";
import { baseUrl } from "../../../../services/config";

const STATUS_OPTIONS = [
  { value: 1, label: "Present" },
  { value: 2, label: "Absent" },
  { value: 3, label: "Casual Leave" },
  { value: 4, label: "Sick Leave" },
  { value: 5, label: "Short Leave" },
  { value: 6, label: "Annual Leave" },
];

const STATUS_COLORS = {
  1: { bg: "#d4edda", color: "#155724" },
  2: { bg: "#f8d7da", color: "#721c24" },
  3: { bg: "#cce5ff", color: "#004085" },
  4: { bg: "#d1ecf1", color: "#0c5460" },
  5: { bg: "#e2e3e5", color: "#383d41" },
  6: { bg: "#d1ecf1", color: "#0c5460" },
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const StatusBadge = ({ value }) => {
  const opt = STATUS_OPTIONS.find((o) => o.value === value);
  const cfg = STATUS_COLORS[value] || { bg: "#e2e3e5", color: "#383d41" };
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.color,
        padding: "3px 12px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {opt?.label || value}
    </span>
  );
};

const MarkEmployeeAttendance = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const campuses = user.campuses || [];
  const campusidget = useSelector((state) => state.campuses.setselectedcampusid);

  // ── Quick single-employee search ──────────────────────────────────────
  const [quickCode, setQuickCode] = useState("");
  const [quickEmp, setQuickEmp] = useState(null);
  const [quickSearching, setQuickSearching] = useState(false);
  const [quickDate, setQuickDate] = useState(todayStr());
  const [quickStatus, setQuickStatus] = useState(2);
  const [quickSaving, setQuickSaving] = useState(false);

  const searchByCode = async () => {
    const code = quickCode.trim();
    if (!code) return;
    setQuickSearching(true);
    setQuickEmp(null);
    try {
      const params = new URLSearchParams({ emp_code: code });
      const res = await fetch(
        `${baseUrl}/manual-attendances/search-by-code?${params}`,
        { headers: { Authorization: `Bearer ${authtoken}` } }
      );
      const data = await res.json();
      if (data.metadata?.success) {
        const emp = Array.isArray(data.payload) ? data.payload[0] : data.payload;
        if (emp) {
          setQuickEmp(emp);
        } else {
          alert.error("No employee found with that code.");
        }
      } else {
        const msg = data.metadata?.message;
        const errorText =
          msg && typeof msg === "object"
            ? Object.values(msg).flat().join(" ")
            : typeof msg === "string"
            ? msg
            : "No employee found with that code.";
        alert.error(errorText);
      }
    } catch (err) {
      console.log(err);
      alert.error("Search failed.");
    } finally {
      setQuickSearching(false);
    }
  };

  const saveQuick = async () => {
    if (!quickEmp || !quickDate) return;
    setQuickSaving(true);
    try {
      const res = await fetch(`${baseUrl}/manual-attendances`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authtoken}`,
        },
        body: JSON.stringify({
          employee_id: quickEmp.id,
          attendance_date: quickDate,
          status: quickStatus,
        }),
      });
      const data = await res.json();
      if (data.metadata?.success) {
        alert.show(`Attendance saved for ${quickEmp.full_name}.`);
        setQuickEmp(null);
        setQuickCode("");
      } else {
        const msg = data.metadata?.message;
        alert.error(typeof msg === "string" ? msg : "Failed to save.");
      }
    } catch (err) {
      console.log(err);
      alert.error("Something went wrong.");
    } finally {
      setQuickSaving(false);
    }
  };

  // ── Bulk mark (campus list + checkboxes) ─────────────────────────────
  const [campusId, setCampusId] = useState(campusidget || "");
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [attendDate, setAttendDate] = useState(todayStr());
  const [status, setStatus] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [submitResults, setSubmitResults] = useState(null);

  useEffect(() => {
    if (campusidget) fetchEmployees(campusidget);
  }, []);

  const fetchEmployees = async (campus) => {
    if (!campus) return;
    setLoadingEmp(true);
    setEmployees([]);
    setSelectedIds([]);
    setSubmitResults(null);
    try {
      const res = await fetch(`${baseUrl}/emp_appointed/${campus}/1`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await res.json();
      if (data.payload) {
        const sorted = [...data.payload].sort((a, b) => a.emp_code - b.emp_code);
        setEmployees(sorted);
      }
    } catch (err) {
      console.log(err);
      alert.error("Failed to load employees.");
    } finally {
      setLoadingEmp(false);
    }
  };

  const handleCampusChange = (id) => {
    setCampusId(id);
    setSearch("");
    setSelectedIds([]);
    setSubmitResults(null);
    dispatch({ type: "SELECTED_CAMPUS_ID", payload: { setselectedcampusid: id } });
    fetchEmployees(id);
  };

  const filtered = employees.filter((e) => {
    const txt = search.toLowerCase();
    return txt
      ? e.full_name?.toLowerCase().includes(txt) || String(e.emp_code).includes(txt)
      : true;
  });

  const allFilteredIds = filtered.map((e) => e.id);
  const allSelected =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openModal = () => {
    setAttendDate(todayStr());
    setStatus(2);
    setSubmitResults(null);
    setShowModal(true);
  };

  const handleBulkSubmit = async () => {
    if (!selectedIds.length) return;
    setSubmitting(true);
    setSubmitResults(null);

    const successes = [];
    const failures = [];

    await Promise.all(
      selectedIds.map(async (employee_id) => {
        try {
          const res = await fetch(`${baseUrl}/manual-attendances`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authtoken}`,
            },
            body: JSON.stringify({ employee_id, attendance_date: attendDate, status }),
          });
          const data = await res.json();
          const emp = employees.find((e) => e.id === employee_id);
          if (data.metadata?.success) {
            successes.push(emp?.full_name || employee_id);
          } else {
            failures.push({
              name: emp?.full_name || employee_id,
              msg: data.metadata?.message || "Failed",
            });
          }
        } catch {
          const emp = employees.find((e) => e.id === employee_id);
          failures.push({ name: emp?.full_name || employee_id, msg: "Network error" });
        }
      })
    );

    setSubmitResults({ successes, failures });
    setSubmitting(false);
    if (successes.length > 0) setSelectedIds([]);
  };

  return (
    <Paper style={{ padding: 20, marginTop: 10 }}>
      <h4 className="mb-4">Mark Employee Attendance</h4>

      {/* ── Quick Single Employee Section ── */}
      <div
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 28,
          background: "#fafafa",
        }}
      >
        <div className="mb-3" style={{ fontWeight: 600, fontSize: 14 }}>
          Quick Mark — Single Employee
        </div>

        <div className="row align-items-end">
          <div className="col-md-3">
            <label className="form-control-label"><b>Employee Code</b></label>
            <input
              className="form-control"
              placeholder="e.g. 19002"
              value={quickCode}
              onChange={(e) => { setQuickCode(e.target.value); setQuickEmp(null); }}
              onKeyDown={(e) => e.key === "Enter" && searchByCode()}
            />
          </div>
          <div className="col-md-2">
            <button
              className="btn btn-success button_blue"
              style={{ width: "auto", minWidth: 90 }}
              onClick={searchByCode}
              disabled={quickSearching || !quickCode.trim()}
            >
              {quickSearching ? <Spinner size="sm" animation="border" /> : "Search"}
            </button>
          </div>
        </div>

        {quickEmp && (
          <div className="mt-3">
            <div
              style={{
                background: "#fff",
                border: "1px solid #dee2e6",
                borderRadius: 6,
                padding: "12px 16px",
                marginBottom: 14,
                display: "flex",
                gap: 24,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span><b>Code:</b> {quickEmp.emp_code}</span>
              <span><b>Name:</b> {quickEmp.full_name}</span>
              {quickEmp.designation?.name && (
                <span><b>Designation:</b> {quickEmp.designation.name}</span>
              )}
              {quickEmp.campus?.name && (
                <span><b>Campus:</b> {quickEmp.campus.name}</span>
              )}
            </div>

            <div className="row align-items-end">
              <div className="col-md-3">
                <label className="form-control-label"><b>Date</b></label>
                <input
                  type="date"
                  className="form-control"
                  value={quickDate}
                  onChange={(e) => setQuickDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-control-label"><b>Status</b></label>
                <select
                  className="form-control"
                  value={quickStatus}
                  onChange={(e) => setQuickStatus(Number(e.target.value))}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-center" style={{ paddingTop: 20, gap: 10 }}>
                <StatusBadge value={quickStatus} />
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-success button_blue"
                  style={{ width: "auto", minWidth: 90 }}
                  onClick={saveQuick}
                  disabled={quickSaving || !quickDate}
                >
                  {quickSaving ? <Spinner size="sm" animation="border" /> : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Bulk Mark Section ── */}
      <div className="mb-3" style={{ fontWeight: 600, fontSize: 14 }}>
        Bulk Mark — Campus Employees
      </div>

      <div className="row mb-3 align-items-end">
        <div className="col-md-4">
          <label className="form-control-label"><b>Campus</b></label>
          <select
            className="form-control"
            value={campusId}
            onChange={(e) => handleCampusChange(e.target.value)}
          >
            <option value="">Select Campus</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-control-label"><b>Search</b></label>
          <input
            className="form-control"
            placeholder="Filter by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-4 d-flex align-items-end">
          <button
            className="btn btn-success button_blue"
            style={{ width: "auto", minWidth: 140 }}
            disabled={selectedIds.length === 0}
            onClick={openModal}
          >
            Mark Attendance
            {selectedIds.length > 0 && (
              <span
                style={{
                  marginLeft: 6,
                  background: "#fff",
                  color: "var(--primary-btn-color)",
                  borderRadius: 10,
                  padding: "1px 7px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {selectedIds.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {loadingEmp ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : !campusId ? (
        <div className="text-center text-muted py-4">
          Select a campus to load employees.
        </div>
      ) : (
        <>
          {filtered.length > 0 && (
            <div className="mb-2" style={{ fontSize: 13, color: "#555" }}>
              {selectedIds.length > 0
                ? `${selectedIds.length} of ${filtered.length} selected`
                : `${filtered.length} employees`}
            </div>
          )}

          <Table responsive bordered hover size="sm">
            <thead className="tablehead">
              <tr className="tableheadtr">
                <th className="background-color-primary tableHEadNAme" style={{ width: 48 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={filtered.length === 0}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th className="background-color-primary tableHEadNAme">#</th>
                <th className="background-color-primary tableHEadNAme">Code</th>
                <th className="background-color-primary tableHEadNAme">Name</th>
                <th className="background-color-primary tableHEadNAme">Designation</th>
                <th className="background-color-primary tableHEadNAme">Department</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {search ? "No employees match your search." : "No employees found."}
                  </td>
                </tr>
              ) : (
                filtered.map((emp, i) => {
                  const isSelected = selectedIds.includes(emp.id);
                  return (
                    <tr
                      key={emp.id}
                      onClick={() => toggleOne(emp.id)}
                      style={{ cursor: "pointer" }}
                      className={isSelected ? "table-active" : ""}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(emp.id)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      <td>{i + 1}</td>
                      <td><b>{emp.emp_code}</b></td>
                      <td>{emp.full_name}</td>
                      <td style={{ fontSize: 12, color: "#555" }}>
                        {emp.designation?.name || "—"}
                      </td>
                      <td style={{ fontSize: 12, color: "#555" }}>
                        {emp.department?.name || "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </>
      )}

      {/* ── Bulk Attendance Modal ── */}
      <Modal show={showModal} onHide={() => !submitting && setShowModal(false)} centered>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">Mark Attendance</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              background: "#f8f9fa",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 20,
              maxHeight: 120,
              overflowY: "auto",
            }}
          >
            <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
              <b>{selectedIds.length} employee{selectedIds.length !== 1 ? "s" : ""} selected</b>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {selectedIds.map((id) => {
                const emp = employees.find((e) => e.id === id);
                return (
                  <span
                    key={id}
                    style={{
                      background: "#e9ecef",
                      borderRadius: 12,
                      padding: "2px 10px",
                      fontSize: 12,
                    }}
                  >
                    {emp?.emp_code} — {emp?.full_name}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-control-label"><b>Attendance Date</b></label>
            <input
              type="date"
              className="form-control"
              value={attendDate}
              onChange={(e) => setAttendDate(e.target.value)}
            />
          </div>

          <div className="mb-2">
            <label className="form-control-label"><b>Status</b></label>
            <select
              className="form-control"
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div className="mt-2">
              <StatusBadge value={status} />
            </div>
          </div>

          {submitResults && (
            <div className="mt-3">
              {submitResults.successes.length > 0 && (
                <div className="alert alert-success py-2" style={{ fontSize: 13 }}>
                  <b>Saved ({submitResults.successes.length}):</b>{" "}
                  {submitResults.successes.join(", ")}
                </div>
              )}
              {submitResults.failures.length > 0 && (
                <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                  <b>Failed ({submitResults.failures.length}):</b>{" "}
                  {submitResults.failures.map((f) => `${f.name} (${f.msg})`).join(", ")}
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {!submitResults ? (
            <>
              <button
                onClick={handleBulkSubmit}
                disabled={submitting || !attendDate}
                className="btn btn-success button_blue"
                style={{ width: "auto", minWidth: 90 }}
              >
                {submitting ? (
                  <><Spinner size="sm" animation="border" className="mr-1" /> Saving…</>
                ) : (
                  `Save (${selectedIds.length})`
                )}
              </button>
              <button
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="btn btn-danger btn_width"
                style={{ width: "auto", minWidth: 90 }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowModal(false)}
              className="btn btn-success button_blue"
              style={{ width: "auto", minWidth: 90 }}
            >
              Done
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </Paper>
  );
};

export default MarkEmployeeAttendance;
